import React from 'react'
import { TableRowData } from '../types'
import { User } from '../../../../api/users'
import { MissionWithSummary, UserWeekSummary, WeekSummary } from '../../../../api/missions'
import { WorkPeriod } from '../../../../api/work-periods'

/**
 * Checks if two work periods overlap on the same day
 */
const workPeriodsOverlapSameDay = (wp1: WorkPeriod, wp2: WorkPeriod): boolean => {
  const date1 = new Date(wp1.start.date).setHours(0, 0, 0, 0)
  const date2 = new Date(wp2.start.date).setHours(0, 0, 0, 0)
  return date1 === date2
}

/**
 * Groups similar missions based on common characteristics
 * Merge missions with the same characteristics into one mission for one row
 * @param missions - Array of missions to group
 * @param excludeGroupKeys - Optional array of groupKeys to exclude from grouping
 */
const mergeSimilarMissionsAsOneRow = (
  missions: MissionWithSummary[],
  excludeGroupKeys: string[] = [],
): (MissionWithSummary & {
  groupedMissions: MissionWithSummary[]
  groupedKey: string
  mainGroupKey: string
})[] => {
  const missionGroups = missions.reduce(
    (groups, mission) => {
      const key = [
        mission.user?._id,
        mission.interimAgency?._id || 'noAgency',
        mission.employer._id,
        mission.service._id,
        mission.jobTitle._id,
      ].join('_')

      // Si la clé est dans excludeGroupKeys, on crée un groupe unique pour cette mission
      const keyIsInExcludeGroupKeys = excludeGroupKeys.includes(key)
      const groupKey = keyIsInExcludeGroupKeys ? `${key}_${mission._id}` : key

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }

      // Find or create a compatible subgroup
      let foundCompatibleGroup = false
      for (const subgroup of groups[groupKey]) {
        const workPeriodsOverlap = mission.workPeriods.some(wp1 =>
          subgroup.workPeriods.some(wp2 => workPeriodsOverlapSameDay(wp1, wp2)),
        )
        if (!workPeriodsOverlap) {
          // Add to existing compatible subgroup
          subgroup.workPeriods.push(...mission.workPeriods)
          subgroup.groupedMissions.push(mission)
          subgroup.start = new Date(
            Math.min(new Date(subgroup.start).getTime(), new Date(mission.start).getTime()),
          )
          subgroup.end = new Date(
            Math.max(new Date(subgroup.end).getTime(), new Date(mission.end).getTime()),
          )

          subgroup.forceNotGrouped = keyIsInExcludeGroupKeys
          subgroup.groupedKey = groupKey
          subgroup.mainGroupKey = key
          foundCompatibleGroup = true
          break
        }
      }

      if (!foundCompatibleGroup) {
        // Create new subgroup
        groups[groupKey].push({
          ...mission,
          workPeriods: [...mission.workPeriods],
          groupedMissions: [mission],
          groupedKey: groupKey,
          mainGroupKey: key,
          forceNotGrouped: keyIsInExcludeGroupKeys,
        })
      }
      return groups
    },
    {} as Record<
      string,
      (MissionWithSummary & {
        groupedMissions: MissionWithSummary[]
        groupedKey: string
        forceNotGrouped: boolean
        mainGroupKey: string
      })[]
    >,
  )

  // Flatten all subgroups
  return Object.values(missionGroups)
    .flat()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
}

/**
 * Transforme les données de WeekSummary en format TableRowData
 * et les trie par nom et prénom de l'utilisateur
 * @param data - Les données de résumé de la semaine
 * @returns Un tableau trié de TableRowData
 */
const useTableData = (users: WeekSummary['users'], excludeGroupKeys: string[]): TableRowData[] =>
  React.useMemo(() => {
    const rows = users.flatMap((userSummary, index) => {
      const mergedSimilairMissionsToOneRow = mergeSimilarMissionsAsOneRow(
        //@ts-ignore
        userSummary.missions,
        excludeGroupKeys,
      )

      const userMissionCount = userSummary.missions.length
      const userMissionCountAfterMerge = mergedSimilairMissionsToOneRow.length
      // On calcule le nombre de lignes à fusionner

      return mergedSimilairMissionsToOneRow.map((mission, index) => {
        const rowSpan =
          index === 0 ? userMissionCount - (userMissionCount - userMissionCountAfterMerge) : 1

        return {
          user: userSummary.user as User,
          mission: mission as UserWeekSummary['missions'][0] & {
            groupedMissions: UserWeekSummary['missions'][0][]
            groupedKey: string
            mainGroupKey: string
          },
          weekIsValidated: userSummary.weekIsValidated,
          interimAgencyOrEmployerId: userSummary.interimAgencyOrEmployerId.toString(),
          costs: userSummary.totals.costs,
          durations: userSummary.totals.durations,
          employerData: userSummary.totals.employerData,
          totals: userSummary.totals,
          rowGroupOptions: {
            groupedKey: mission.groupedKey,
            isFirstMission: index === 0,
            totalMissions: mission.groupedMissions.length,
            rowSpan,
          },
          userMissionsCount: userMissionCount,
          counters: userSummary.counters,
        } as TableRowData
      })
    })

    return rows
  }, [users, excludeGroupKeys])

export default useTableData
